import { axiosInstance, API_ENDPOINTS } from "config/api";
import { cachedApiCall, getCacheKey, clearCacheByPattern } from "utils/apiCache";

/**
 * Appointment Service
 * Centralized service for all appointment-related API calls with caching
 */

/**
 * Fetch doctor consultations with caching and deduplication
 * @param {number} doctorId - Doctor ID
 * @param {string} fromDate - Start date (YYYY-MM-DD)
 * @param {string} toDate - End date (YYYY-MM-DD)
 * @param {Object} options - Cache options { force: boolean, ttl: number }
 * @returns {Promise<Object>} - Consultations data
 */
export const fetchDoctorConsultations = async (doctorId, fromDate, toDate, options = {}) => {
  const cacheKey = getCacheKey('appointment/doctorConsultations', {
    doctor_id: doctorId,
    from_date: fromDate,
    to_date: toDate
  });

  // Use 1-minute TTL for consultation data (needs to be fresh)
  const ttl = options.ttl || 1 * 60 * 1000; // 1 minute

  return cachedApiCall(
    cacheKey,
    async () => {
      const endpoint = `${API_ENDPOINTS.APPOINTMENT.DOCTOR_CONSULTATIONS}?doctor_id=${doctorId}&from_date=${fromDate}&to_date=${toDate}`;
      console.log(`[AppointmentService] Fetching consultations: ${endpoint}`);
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      
      return response.data;
    },
    { ttl, force: options.force || false }
  );
};

/**
 * Fetch appointments by date range for a doctor
 * @param {number} doctorId - Doctor ID
 * @param {string} fromDate - Start date (YYYY-MM-DD)
 * @param {string} toDate - End date (YYYY-MM-DD)
 * @param {Object} options - Cache options
 * @returns {Promise<Array>} - Appointments data
 */
export const fetchAppointmentsByDateRangeByDoctor = async (doctorId, fromDate, toDate, options = {}) => {
  const cacheKey = getCacheKey('appointment/byDateRangeByDoctor', {
    doctor_id: doctorId,
    from_date: fromDate,
    to_date: toDate
  });

  const ttl = options.ttl || 1 * 60 * 1000; // 1 minute

  return cachedApiCall(
    cacheKey,
    async () => {
      const response = await axiosInstance.post(
        "/api/appointment/getAppointmentsByDateRangeByDoctor",
        {
          doctor_id: doctorId,
          from_date: fromDate,
          to_date: toDate,
        }
      );
      
      return response.data;
    },
    { ttl, force: options.force }
  );
};

/**
 * Fetch past appointments for a patient
 * @param {number} patientId - Patient ID
 * @param {Object} options - Cache options
 * @returns {Promise<Array>} - Past appointments
 */
export const fetchPastAppointments = async (patientId, options = {}) => {
  const cacheKey = getCacheKey('appointment/past/patient', { patient_id: patientId });
  
  // Longer TTL for past appointments (5 minutes)
  const ttl = options.ttl || 5 * 60 * 1000;

  return cachedApiCall(
    cacheKey,
    async () => {
      const response = await axiosInstance.get(
        `api/appointment/past/patient?patient_id=${patientId}`
      );
      return response.data;
    },
    { ttl, force: options.force }
  );
};

/**
 * Update appointment status
 * Invalidates related caches after update
 * @param {number} appointmentId - Appointment ID
 * @param {string} status - New status
 * @returns {Promise<Object>} - Update response
 */
export const updateAppointmentStatus = async (appointmentId, status) => {
  const response = await axiosInstance.post('/api/appointment/updateApptStatus', {
    id: appointmentId,
    status: status
  });

  // Invalidate consultation caches (all doctors, all date ranges)
  clearCacheByPattern('appointment/doctorConsultations');
  clearCacheByPattern('appointment/byDateRangeByDoctor');
  console.log(`[AppointmentService] Cache invalidated after status update for appointment ${appointmentId}`);

  return response.data;
};

/**
 * Invalidate all appointment-related caches
 * Useful for manual refresh
 */
export const invalidateAppointmentCaches = () => {
  clearCacheByPattern('appointment/');
  console.log('[AppointmentService] All appointment caches invalidated');
};

export default {
  fetchDoctorConsultations,
  fetchAppointmentsByDateRangeByDoctor,
  fetchPastAppointments,
  updateAppointmentStatus,
  invalidateAppointmentCaches,
};
